// frontend/src/utils/formatUtils.js

const DEFAULT_LOCALE = 'en-US';
const DEFAULT_CURRENCY_CODE = 'USD';
const DEFAULT_NULL_DISPLAY = '-';

/**
 * Formats a date value to DD-MMM-YYYY.
 * Example: new Date(2023, 0, 1) -> "01-Jan-2023"
 * @param {string|Date} value - The date value to format.
 * @returns {string} Formatted date string or default null display.
 */
function formatDateValue(value) {
  if (value === null || value === undefined || value === '') return DEFAULT_NULL_DISPLAY;
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return DEFAULT_NULL_DISPLAY;

    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString(DEFAULT_LOCALE, { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (e) {
    console.error("Error formatting date:", e);
    return DEFAULT_NULL_DISPLAY;
  }
}

/**
 * Formats a number to a specified number of decimal places.
 * @param {number|string} value - The numeric value.
 * @param {number} precision - Number of decimal places (e.g., 2, 3, 4).
 * @returns {string} Formatted number string or default null display.
 */
function formatDecimalValue(value, precision) {
  if (value === null || value === undefined || value === '') return DEFAULT_NULL_DISPLAY;
  const num = Number(value);
  if (isNaN(num)) return String(value); // Return as is if not a valid number
  return num.toFixed(precision);
}

/**
 * Formats a number as localized currency.
 * @param {number|string} value - The numeric value.
 * @param {string} locale - The locale string (e.g., 'en-US', 'th-TH').
 * @param {string} currencyCode - The currency code (e.g., 'USD', 'THB').
 * @returns {string} Formatted currency string or default null display.
 */
function formatCurrencyValue(value, locale, currencyCode) {
  if (value === null || value === undefined || value === '') return DEFAULT_NULL_DISPLAY;
  const num = Number(value);
  if (isNaN(num)) return String(value); 

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
    }).format(num);
  } catch (e) {
    console.error("Error formatting currency:", e);
    // Fallback for invalid currency codes or other Intl errors
    return `${currencyCode} ${num.toFixed(2)}`; 
  }
}

/**
 * Centralized function to format a value for display based on column configuration.
 * @param {*} value - The raw value to format.
 * @param {object} columnConfig - The column configuration object, containing at least a 'type' and optionally 'unit'.
 * @param {object} [options] - Optional parameters.
 * @param {string} [options.locale=DEFAULT_LOCALE] - Locale for currency formatting.
 * @param {string} [options.currencyCode=DEFAULT_CURRENCY_CODE] - Currency code for currency formatting.
 * @param {string} [options.nullDisplay=DEFAULT_NULL_DISPLAY] - String to display for null/undefined values.
 * @returns {string} The formatted display string.
 */
export function formatDisplayValue(value, columnConfig, options = {}) {
  const { 
    locale = DEFAULT_LOCALE, 
    currencyCode = DEFAULT_CURRENCY_CODE, 
    nullDisplay = DEFAULT_NULL_DISPLAY 
  } = options;

  if (value === null || value === undefined || value === '') {
    return nullDisplay;
  }

  const type = columnConfig && columnConfig.type ? String(columnConfig.type).toLowerCase() : 'string';

  switch (type) {
    case 'date':
      return formatDateValue(value);
    case 'datetime': // Falls back to date-only for now, can be expanded
      return formatDateValue(value); 
    case 'decimal2':
      return formatDecimalValue(value, 2);
    case 'decimal3':
      return formatDecimalValue(value, 3);
    case 'decimal4':
      return formatDecimalValue(value, 4);
    case 'currency':
      // Use columnConfig.unit as currencyCode if it's a 3-letter string, otherwise default
      const ccy = columnConfig && columnConfig.unit && columnConfig.unit.length === 3 
                    ? columnConfig.unit.toUpperCase()
                    : currencyCode;
      return formatCurrencyValue(value, locale, ccy);
    case 'int':
      const intNum = parseInt(value, 10);
      return isNaN(intNum) ? String(value) : intNum.toLocaleString(locale);
    case 'float':
      const floatNum = parseFloat(value);
      return isNaN(floatNum) ? String(value) : floatNum.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    case 'comma': // Generic number formatting with commas
         const commaNum = Number(value);
         if (isNaN(commaNum)) return String(value);
         // if unit is $, treat as currency but without specific code, just prefix
         if (columnConfig && columnConfig.unit === '$') {
            return `$` + commaNum.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
         }
         return commaNum.toLocaleString(locale);
    case 'boolean':
      return String(value) === 'true' || String(value) === '1' ? 'Yes' : 'No';
    case 'text':
    case 'textselectable':
    case 'selectable':
    case 'textarea':
    case 'string':
    default:
      return String(value);
  }
} 